/**
 * Route-level ErrorBoundary helper. Fires TWO captures whenever a
 * route boundary mounts with an error:
 *
 *   1. `posthog.captureException()` for the dashboard's exception
 *      stream and the alert pipeline.
 *   2. `POST /api/admin-notify` for direct admin notification with
 *      a reference ID we surface to the user.
 *
 * The two channels are deliberate. PostHog has ingestion lag and
 * (for end-users) ad blockers; the admin-notify route runs on our
 * own infra. Belt-and-braces.
 *
 * Returns `{ referenceId }` so the boundary UI can render it for the
 * user ("Tell us about this and quote ref: ...").
 *
 * Usage:
 *   export function ErrorBoundary() {
 *     const { referenceId } = useReportRouteError("expenses");
 *     return <Oops referenceId={referenceId} />;
 *   }
 */
import { useEffect, useState } from "react";
import { isRouteErrorResponse, useLocation, useRouteError } from "react-router";
import { reportError } from "./analytics";

export interface UseReportRouteErrorResult {
  /** Server-issued admin-notification row id. May be null briefly
   *  on first render and on failure to reach the notify endpoint. */
  referenceId: string | null;
}

/**
 * Turns the dev-facing route key (e.g. "_app", "work.$id_.quote",
 * "settings.services", "_public.for.$trade") into something a person
 * reading the admin notifications dropdown can parse at a glance:
 *
 *   "_app"                  -> "App shell"
 *   "root"                  -> "Site root"
 *   "work.$id_.quote"       -> "Quote editor"
 *   "clients.$id"           -> "Customer detail"
 *   "settings.services"     -> "Settings: Services"
 *   "_public.for.$trade"    -> "Public trade page"
 *   "s.$token"              -> "Booking link"
 *
 * Anything we haven't hand-rolled falls back to a generic Title Case
 * split. Better one ugly fallback than letting raw "_app" leak.
 */
function humaniseRouteName(raw: string): string {
  const overrides: Record<string, string> = {
    _app: "App shell",
    root: "Site root",
    admin: "Admin",
    "work.$id": "Job detail",
    "work.$id_.quote": "Quote editor",
    "clients.$id": "Customer detail",
    "properties.$id": "Property detail",
    "help.messages.$id": "Help message",
    "s.$token": "Booking link",
    "_public.for.$trade": "Public trade page",
  };
  if (overrides[raw]) return overrides[raw];

  // Strip leading underscores ("_app", "_public") and "$"-prefixed
  // route params (they're React-Router internals, never useful copy).
  const cleaned = raw
    .split(".")
    .map((s) => s.replace(/^_+/, "").replace(/^\$.*$/, "").replace(/_+$/, ""))
    .filter(Boolean);
  if (cleaned.length === 0) return "App";

  const titled = cleaned.map(
    (s) =>
      s.charAt(0).toUpperCase() +
      s.slice(1).replace(/-([a-z])/g, (_m, c) => " " + c.toUpperCase()),
  );
  return titled.join(": ");
}

export function useReportRouteError(
  routeName: string,
): UseReportRouteErrorResult {
  const error = useRouteError();
  const location = useLocation();
  const [referenceId, setReferenceId] = useState<string | null>(null);

  useEffect(() => {
    if (!error) return;

    const isResponse = isRouteErrorResponse(error);
    const status = isResponse ? error.status : null;

    // Skip routine HTTP statuses that aren't bugs.
    const skipStatus = status === 401 || status === 403 || status === 404;

    const routeError = isResponse
      ? new Error(`Route error ${error.status} ${error.statusText}`)
      : error instanceof Error
        ? error
        : new Error(typeof error === "string" ? error : "Unknown route error");

    // 1. PostHog (always, even for skipped statuses -- the trend is
    //    still useful even if we don't page Barry).
    reportError(routeError, {
      $exception_source: "route_error_boundary",
      route_name: routeName,
      pathname: location.pathname,
      search: location.search,
      route_error_status: status,
      route_error_data: isResponse ? error.data : null,
    });

    // 2. Direct admin-notify -- only for real errors. Public-route
    //    crashes don't ping the phone either.
    if (skipStatus || location.pathname.startsWith("/_public")) return;

    // Human-readable title for the admin notifications dropdown.
    // Falls through to the raw routeName via humaniseRouteName so
    // dev-jargon ("_app", "$id") never reaches Barry's inbox surface.
    const errorMessage = routeError.message.slice(0, 200);
    const humanisedRoute = humaniseRouteName(routeName);
    const title = `${humanisedRoute}: ${errorMessage}`;
    // Dedupe still keys on the raw routeName + message so the same
    // error from the same route folds together regardless of how the
    // humaniser renders it.
    const dedupeKey = `route_error::${routeName}::${errorMessage}`;
    const body = JSON.stringify(
      {
        message: routeError.message,
        stack: routeError.stack?.split("\n").slice(0, 12).join("\n"),
        pathname: location.pathname,
        search: location.search,
        status,
      },
      null,
      2,
    ).slice(0, 3500);

    fetch("/api/admin-notify", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        kind: "route_error",
        severity: "error",
        title,
        body,
        linkTo: `${location.pathname}${location.search}`,
        dedupeKey,
      }),
    })
      .then(async (resp) => {
        if (!resp.ok) return;
        const data = await resp.json().catch(() => null);
        if (data?.ok && typeof data.id === "string") {
          setReferenceId(data.id);
        }
      })
      .catch(() => {
        // Network failure: PostHog still gets it via path #1.
      });
  }, [error, routeName, location.pathname, location.search]);

  return { referenceId };
}
